import { Injectable, inject } from '@angular/core';
import { BrandingService, TenantSlugService } from '@bodyanalytics/ui-core';

import { TenantContextStore } from './tenant-context.store';

/**
 * Calendar-portal tenant bootstrap + slug-aware link building.
 *
 * `initialize()` is the app-initializer entry point
 * (see `apps/front/calendar/src/app/app.config.ts`):
 *   - no slug in the URL → resolves immediately; the portal boots with the
 *     stock AppliedBioX branding — ZERO behavior change for existing links;
 *   - `/t/:slug` (or legacy `?t=<slug>`) → fetches branding by slug through
 *     the shared BrandingService (native fetch — safe before Auth0/HttpClient
 *     exist) and seeds the TenantContextStore with the resolved tenant.
 *
 * The slug itself comes from ui-core's `TenantSlugService` (captured from
 * `document.location` pre-router), so this service, the store, and the
 * public-event fetchers all agree on one parse.
 */
@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private readonly tenantSlug = inject(TenantSlugService);
  private readonly branding = inject(BrandingService);
  private readonly store = inject(TenantContextStore);

  /** Never rejects — a broken branding fetch must not block bootstrap. */
  async initialize(): Promise<void> {
    const slug = this.tenantSlug.slug;
    if (!slug) {
      return; // default portal — leave the store empty, no branding fetch
    }

    this.store.setSlug(slug);
    const context = await this.branding.initBySlug(slug);
    if (context) {
      this.store.setResolvedTenant(context.tenantId);
    } else {
      // Unknown slug or fetch failure: defaults were applied by BrandingService.
      this.store.markBrandingLoaded();
    }
  }

  /**
   * Prefix an absolute in-app path with the active tenant mount, so
   * slug-aware navigation keeps the `/t/:slug` prefix (deep links survive a
   * hard refresh with the tenant theme intact).
   *
   *   link('/calendar')       → '/t/iron-athletics/calendar'   (tenant portal)
   *   link('calendar')        → '/t/iron-athletics/calendar'
   *   link('/booking-flow/x') → '/booking-flow/x'              (default portal)
   */
  link(path: string): string {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${this.store.routePrefix()}${normalized}`;
  }

  /**
   * routerLink-commands variant of `link` for `[routerLink]` bindings:
   *   commands('booking-flow', eventId) → ['/t', slug, 'booking-flow', eventId]
   */
  commands(...segments: string[]): string[] {
    const slug = this.store.slug();
    return slug ? ['/t', slug, ...segments] : ['/', ...segments];
  }
}
